import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../ui/card';
import { QrCode, RefreshCw, MapPin } from 'lucide-react';
import Pagination from '../shared/Pagination';
import { API } from '../../../lib/api';

const PAGE_SIZE = 10;

const ScansTab = ({ partners, getAuthHeaders }) => {
  const [selectedPartner, setSelectedPartner] = useState('all');
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchScans = useCallback(async (partnerId) => {
    setLoading(true);
    setCurrentPage(1);
    try {
      if (partnerId === 'all') {
        const results = await Promise.all(
          partners.map(p => axios.get(`${API}/partners/${p.id}/scans`, getAuthHeaders()).then(r => r.data))
        );
        setScans(results.flat().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      } else {
        const res = await axios.get(`${API}/partners/${partnerId}/scans`, getAuthHeaders());
        setScans(res.data);
      }
    } catch {
      toast.error('Erro ao carregar leituras');
    } finally {
      setLoading(false);
    }
  }, [partners, getAuthHeaders]); // eslint-disable-line

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (partners.length > 0) fetchScans(selectedPartner); }, [selectedPartner, partners.length]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getPartnerName = (scan) => {
    const p = partners.find(p => p.id === scan.partner_id);
    return p?.name || scan.partner_slug;
  };

  const deviceIcon = (d = '') => {
    const dl = d.toLowerCase();
    if (dl.includes('iphone') || dl.includes('ipad') || dl.includes('android') || dl.includes('mobile')) return '📱';
    if (dl.includes('tablet') || dl.includes('ipad')) return '📲';
    return '💻';
  };

  const totalPages = Math.ceil(scans.length / PAGE_SIZE);
  const paginatedScans = scans.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-800">Leituras de QR Code ({scans.length})</h2>
        <div className="flex items-center gap-3">
          <select
            value={selectedPartner}
            onChange={(e) => setSelectedPartner(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Todos os parceiros</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={() => fetchScans(selectedPartner)} className="p-2 text-gray-400 hover:text-orange-600 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : scans.length === 0 ? (
        <Card className="p-12 text-center">
          <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nenhuma leitura registrada ainda.</p>
        </Card>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 border-b">
                  <th className="px-4 py-3">Data / Hora</th>
                  <th className="px-4 py-3">Parceiro</th>
                  <th className="px-4 py-3">Dispositivo</th>
                  <th className="px-4 py-3">Navegador</th>
                  <th className="px-4 py-3">Cidade</th>
                  <th className="px-4 py-3">Regiao</th>
                  <th className="px-4 py-3">Pais</th>
                </tr>
              </thead>
              <tbody>
                {paginatedScans.map((scan, i) => (
                  <tr key={scan.id || i} className="border-b last:border-0 hover:bg-orange-50/40 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">{formatDate(scan.timestamp)}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{getPartnerName(scan)}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5">
                        <span className="text-base leading-none">{deviceIcon(scan.device)}</span>
                        <span className="text-gray-700">{scan.device || '—'}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{scan.browser || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />{scan.city || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{scan.region || '—'}</td>
                    <td className="px-4 py-3">
                      {scan.country
                        ? <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">{scan.country}</span>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={scans.length}
            pageSize={PAGE_SIZE}
          />
        </div>
      )}
    </div>
  );
};

export default ScansTab;
